import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageLink, HrefLink } from '@/components';
import {
  OnboardingResultsAreIn,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingCostFactors,
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingCarePhasesPhaseTwo,
  OnboardingCareSettingsPhaseTwo,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingCarePhasesPhaseThree,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesSummary,
  OnboardingFundingSources,
  OnboardingFundingCoverage,
  OnboardingDownloadReport,
  OnboardingPolicyApplicationDashboard,
  OnboardingOverview,
} from '@/pages';
import { OnboardingNavigationMenu } from '@/components/OnboardingNavigationMenu';

import { ChevronRightIcon } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import React, { FunctionComponent, useState, useMemo } from 'react';
import { useSupportProviderSyncOnExit } from '@/hooks/useSupportProviderSyncOnExit';

import {
  selectClient,
  selectSession,
  selectFeatureFlags,
  selectSessionAdvisor,
  selectClientSupportProviderSet,
} from '@/model';
import { cn } from '@client';
import { selectSlides } from '@/components/OnboardingSlideInfo';
import { CareEnvironment, CarePhase, carePhaseDefs, SessionType } from '@shared';

type OnboardingSlideNavigationProps = {
  slidePage: FunctionComponent<ClientIdProps>;
  nextButtonLabel: string;
  nextSlidePage?: FunctionComponent<ClientIdProps>;
  secondaryNextSlidePage?: FunctionComponent<ClientIdProps>;
  secondaryNextButtonLabel?: string;
  optionalCtaLabel?: string;
  optionalCtaPage?: FunctionComponent<ClientIdProps>;
  previousSlidePage?: FunctionComponent<ClientIdProps>;
  disableNextButton?: boolean;
  shouldHideNextNavigation?: boolean;
  hideNavigationMenu?: boolean;
  showErrorToast: (message?: string) => void;
  onNextClick?: () => void;
  className?: string;
};

const slideTitles = new Map<FunctionComponent<ClientIdProps>, string>([
  [OnboardingOverview, 'Overview'],
  [OnboardingResultsAreIn, 'Results Are In'],
  [OnboardingOurPredictions, 'Our Predictions'],
  [OnboardingPredictedCareNeeds, 'Predicted Care Needs'],
  [OnboardingCostFactors, 'Estimated LTC Costs'],
  [OnboardingCarePhasesPhaseOne, 'Early Care Phase'],
  [OnboardingCareSettingsPhaseOne, 'Early Care Settings'],
  [OnboardingFamilyCaregivingPhaseOne, 'Early Family Caregiving'],
  [OnboardingCarePhasesPhaseTwo, 'Moderate Care Phase'],
  [OnboardingCareSettingsPhaseTwo, 'Moderate Care Settings'],
  [OnboardingFamilyCaregivingPhaseTwo, 'Moderate Family Caregiving'],
  [OnboardingCarePhasesPhaseThree, 'Full Care Phase'],
  [OnboardingCareSettingsPhaseThree, 'Full Care Settings'],
  [OnboardingFamilyCaregivingPhaseThree, 'Full Family Caregiving'],
  [OnboardingCarePhasesSummary, 'Cost Total'],
  [OnboardingFundingSources, 'Financing Plan'],
  [OnboardingFundingCoverage, 'Funding Coverage'],
  [OnboardingPolicyApplicationDashboard, 'Application Review'],
  [OnboardingDownloadReport, 'Download Report'],
]);

export function OnboardingSlideNavigation({
  slidePage,
  shouldHideNextNavigation,
  disableNextButton,
  nextButtonLabel,
  nextSlidePage,
  optionalCtaLabel,
  optionalCtaPage,
  secondaryNextSlidePage,
  secondaryNextButtonLabel,
  previousSlidePage,
  showErrorToast,
  hideNavigationMenu,
  onNextClick,
  className,
}: OnboardingSlideNavigationProps) {
  const advisor = useSelector(selectSessionAdvisor);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const fromLtcPlanning = searchParams.get('fromLtcPlanning') === 'true';
  const client = useSelector(selectClient)!;
  const clientSupportProviderSet = useSelector(selectClientSupportProviderSet);
  const { sessionType } = useSelector(selectSession);
  const {
    careEnvironmentSelections,
    intakeSurvey: { clientHasStartedLtc, clientCurrentCarePhase },
  } = client;

  const NAV_BUTTON_STYLE = cn(
    'max-h-16 md:min-w-32 rounded-full px-6 py-4 text-base text-white md:px-8 md:py-6 md:text-lg bg-primary hover:bg-primary-hover m-0',
  );

  const { clientFirstName, clientLastName, clientId, clientEmail, advisorId, surveys } = client;

  const featureFlags = useSelector(selectFeatureFlags);

  const selectedSlides = useMemo(() => {
    return selectSlides(client, featureFlags).filter(slide => {
      // Exclude Overview slide for client sessions
      if (slide === OnboardingOverview && sessionType === SessionType.client) {
        return false;
      }

      if (slideIsNotCarePlanningSlide(slide)) {
        return true;
      }

      if (excludeFamilyCareGivingNonHomeCarePhases(slide, careEnvironmentSelections)) {
        return false;
      }

      if (clientHasStartedLtc && clientCurrentCarePhase) {
        return !excludePreviousCarePhaseSlidesForOnClaim(slide, clientCurrentCarePhase);
      }

      return true;
    });
  }, [
    client,
    featureFlags,
    clientHasStartedLtc,
    clientCurrentCarePhase,
    careEnvironmentSelections,
    sessionType,
  ]);

  const relativeSlideIndex = useMemo(() => {
    const overviewSlideDefault = advisor.defaultClientOnboardingOverviewSlide ?? true;

    if (overviewSlideDefault) {
      return selectedSlides.indexOf(slidePage);
    }

    const filteredSlides = selectedSlides.slice(1);

    return filteredSlides.indexOf(slidePage);
  }, [selectedSlides, slidePage]);

  const surveyId = useMemo(() => getLastSurveyId(surveys), [surveys]);

  const feedbackSurveyUrl = useMemo(
    () =>
      `https://waterlily.typeform.com/to/m4H0GpSY#consumer_email=${clientEmail}&consumer_first_name=${clientFirstName}&advisor_id=${advisorId}&survey_id=${surveyId}&organization_name=xxxxx&consumer_last_name=${clientLastName}`,
    [clientEmail, clientFirstName, advisorId, surveyId, clientLastName],
  );

  const { updateClientSupportProviderSet } = useSupportProviderSyncOnExit(clientSupportProviderSet);

  const handleNextButtonClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (disableNextButton) {
      e.preventDefault();
      showErrorToast();
      return;
    }

    onNextClick?.();
    updateClientSupportProviderSet(clientSupportProviderSet);
  };

  const secondaryNextButton =
    secondaryNextSlidePage && secondaryNextButtonLabel ? (
      <PageLink
        className="w-full md:w-auto"
        to={secondaryNextSlidePage}
        targetProps={{ clientId }}
      >
        <Button
          variant="outline"
          className={cn(
            NAV_BUTTON_STYLE,
            'w-full bg-white text-gray-800 hover:bg-gray-100 md:w-auto',
          )}
        >
          {secondaryNextButtonLabel}
        </Button>
      </PageLink>
    ) : null;

  const feedbackSurveyNextButton = (
    <HrefLink
      to={feedbackSurveyUrl}
      rel="noopener"
      target="_blank"
    >
      <Button
        className={cn(
          'flex flex-col items-center gap-2',
          NAV_BUTTON_STYLE,
          disableNextButton ? 'cursor-not-allowed' : '',
        )}
        size="lg"
        disabled={disableNextButton}
      >
        {nextButtonLabel}
        {secondaryNextButton}
      </Button>
    </HrefLink>
  );

  const nextSlideButton = (
    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-2">
      {secondaryNextButton}
      <PageLink
        className=""
        onClick={handleNextButtonClick}
        to={getPrevOrNextSlide(selectedSlides, slidePage, 'next', nextSlidePage)}
        targetProps={{ clientId }}
        data-testid="onboarding-next-link"
      >
        <Button
          className={cn(
            NAV_BUTTON_STYLE,
            'text-white',
            disableNextButton ? 'cursor-not-allowed' : '',
          )}
          data-intercom-target="Next Button"
          data-testid="onboarding-next-button"
          disabled={disableNextButton}
        >
          <div className="flex items-center gap-1">
            {nextButtonLabel}
            <ChevronRightIcon className="h-6 w-6" />
          </div>
        </Button>
      </PageLink>
    </div>
  );
  const nextButton = shouldHideNextNavigation
    ? null
    : slidePage === OnboardingDownloadReport
      ? feedbackSurveyNextButton
      : nextSlideButton;

  const backButton = fromLtcPlanning ? (
    <Button
      variant="outline"
      className={cn(NAV_BUTTON_STYLE, 'bg-white text-gray-800 hover:bg-gray-100')}
      onClick={() => {
        window.history.back();
      }}
    >
      Back
    </Button>
  ) : (
    <PageLink
      onClick={() => updateClientSupportProviderSet(clientSupportProviderSet)}
      to={getPrevOrNextSlide(selectedSlides, slidePage, 'prev', previousSlidePage)}
      targetProps={{ clientId }}
      data-testid="onboarding-back-link"
    >
      <Button
        variant="outline"
        className={cn(NAV_BUTTON_STYLE, 'bg-white text-gray-800 hover:bg-gray-100')}
        data-testid="onboarding-back-button"
      >
        Back
      </Button>
    </PageLink>
  );

  const optionalSlideCtaButton = optionalCtaLabel && optionalCtaPage && (
    <PageLink
      to={optionalCtaPage}
      targetProps={{ clientId }}
    >
      <Button
        variant="outline"
        className={cn(NAV_BUTTON_STYLE, 'relative bg-white text-gray-800 hover:bg-gray-100')}
        data-testid="onboarding-optional-button"
      >
        <Badge
          variant="secondary"
          className="absolute right-0 top-0 -translate-y-1/2 border-2 border-white"
        >
          Optional
        </Badge>
        {optionalCtaLabel}
      </Button>
    </PageLink>
  );

  return (
    <>
      {!hideNavigationMenu && (
        <OnboardingNavigationMenu
          selectedSlides={selectedSlides}
          relativeSlideIndex={relativeSlideIndex}
          slideTitles={slideTitles}
          client={client}
          careEnvironmentSelections={careEnvironmentSelections}
          sessionType={sessionType}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          currentSlidePage={slidePage}
          showOverviewSlide={
            sessionType !== SessionType.client &&
            (advisor.defaultClientOnboardingOverviewSlide ?? true)
          }
        />
      )}

      {/* Bottom Navigation - always show */}
      <div className="fixed bottom-0 left-0 right-0 z-[10] w-full md:left-1/2 md:w-fit md:-translate-x-1/2">
        <div
          className={cn(
            'flex flex-row flex-wrap items-end gap-3 bg-white p-[30px] !pr-24 shadow-[0_-2px_4px_rgba(0,0,0,0.1)] md:mb-4 md:flex-nowrap md:gap-4 md:rounded-full md:!pr-4 md:shadow-lg',
            'absolute bottom-0 left-0 right-0',
            className,
          )}
        >
          {relativeSlideIndex !== 0 && backButton}
          {optionalSlideCtaButton}
          {nextButton}
        </div>
      </div>
    </>
  );
}

function getPrevOrNextSlide(
  selectedSlides: FunctionComponent<ClientIdProps>[],
  slidePage: FunctionComponent<ClientIdProps>,
  direction: 'next' | 'prev',
  prevOrNextSlide?: FunctionComponent<ClientIdProps>,
) {
  if (prevOrNextSlide && selectedSlides.includes(prevOrNextSlide)) {
    return prevOrNextSlide;
  }
  const slideIndex = selectedSlides.indexOf(slidePage);
  const prevOrNextIndex = slideIndex + (direction === 'next' ? 1 : -1);
  return selectedSlides[prevOrNextIndex];
}

export function getLastSurveyId(surveys: Survey[]) {
  if (surveys && surveys.length > 0) {
    return surveys[surveys.length - 1].surveyId;
  }
}

function slideIsNotCarePlanningSlide(slide: FunctionComponent<ClientIdProps>): boolean {
  const carePlanningSlides: FunctionComponent<ClientIdProps>[] = [
    OnboardingCarePhasesPhaseOne,
    OnboardingCarePhasesPhaseTwo,
    OnboardingCarePhasesPhaseThree,
    OnboardingCareSettingsPhaseOne,
    OnboardingCareSettingsPhaseTwo,
    OnboardingCareSettingsPhaseThree,
    OnboardingFamilyCaregivingPhaseOne,
    OnboardingFamilyCaregivingPhaseTwo,
    OnboardingFamilyCaregivingPhaseThree,
  ];

  return !carePlanningSlides.includes(slide);
}

function excludePreviousCarePhaseSlidesForOnClaim(
  slide: FunctionComponent<ClientIdProps>,
  clientCurrentCarePhase: CarePhase,
): boolean {
  const currentPhaseIndex = carePhaseDefs[clientCurrentCarePhase].index;

  if (
    slide === OnboardingCarePhasesPhaseOne ||
    slide === OnboardingCareSettingsPhaseOne ||
    slide === OnboardingFamilyCaregivingPhaseOne
  ) {
    return currentPhaseIndex > 0;
  }

  if (
    slide === OnboardingCarePhasesPhaseTwo ||
    slide === OnboardingCareSettingsPhaseTwo ||
    slide === OnboardingFamilyCaregivingPhaseTwo
  ) {
    return currentPhaseIndex > 1;
  }

  if (
    slide === OnboardingCarePhasesPhaseThree ||
    slide === OnboardingCareSettingsPhaseThree ||
    slide === OnboardingFamilyCaregivingPhaseThree
  ) {
    return false;
  }

  return false;
}

function excludeFamilyCareGivingNonHomeCarePhases(
  slide: FunctionComponent<ClientIdProps>,
  careEnvironmentSelections?: Partial<Record<CarePhase, CareEnvironment | null>>,
): boolean {
  if (slide === OnboardingFamilyCaregivingPhaseOne) {
    return careEnvironmentSelections?.[CarePhase.earlyCare] !== CareEnvironment.home;
  }

  if (slide === OnboardingFamilyCaregivingPhaseTwo) {
    return careEnvironmentSelections?.[CarePhase.moderateCare] !== CareEnvironment.home;
  }

  if (slide === OnboardingFamilyCaregivingPhaseThree) {
    return careEnvironmentSelections?.[CarePhase.fullCare] !== CareEnvironment.home;
  }
  return false;
}
